import { useFormContext } from 'react-hook-form';

import { theme } from '@theguarantors/tg-package-material-ui';

import { AddressesAutocomplete } from '@/components/AddressesAutocomplete';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';

import { useLeaseDetailsUI } from '../../lease-details-ui.context';

// Walks an RHF error node (which for an object field can be nested, e.g.
// `address.monolith_building_id`) and returns the first message it finds.
const firstErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const node = error as { message?: unknown };
  if (typeof node.message === 'string') return node.message;
  for (const key of Object.keys(error)) {
    const found = firstErrorMessage((error as Record<string, unknown>)[key]);
    if (found) return found;
  }
  return undefined;
};

export type LeaseAddressInputProps = {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  showLoadingSkeleton?: boolean;
  onSelect?: (option: AddressAutocompleteOption | null) => void;
};

// Address type-ahead bound to RHF via `useFormContext`. Stores the whole
// selected option object in form state (the engine's only object-valued field)
// and feeds the backend-connected `AddressesAutocomplete` the
// `monolith_building_id` it matches on, mirroring the legacy invite binding.
export const LeaseAddressInput = ({
  name,
  label,
  required = false,
  disabled = false,
  showLoadingSkeleton = true,
  onSelect,
}: LeaseAddressInputProps) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { inputSize } = useLeaseDetailsUI();
  const selected = watch(name) as { monolith_building_id?: number } | null | undefined;
  const helperText = firstErrorMessage(getFieldState(name, formState).error);

  return (
    <AddressesAutocomplete
      color="secondary"
      size={inputSize}
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      // `AddressesAutocomplete` types `value` as a required number but treats
      // a falsy value as "no selection" (`if (!value)` guards internally) — the
      // component's own tests use `value={0}` as that sentinel. Match that
      // convention here so we're not casting `undefined as number`.
      value={selected?.monolith_building_id ?? 0}
      showLoadingSkeleton={showLoadingSkeleton}
      error={Boolean(helperText)}
      helperText={helperText}
      onBlur={() => trigger(name)}
      onChange={(option) => {
        setValue(name, option, { shouldValidate: true, shouldDirty: true });
        trigger(name);
        onSelect?.(option);
      }}
      inputSx={{
        '& .MuiFormLabel-asterisk': { color: `${theme.palette.red[600]} !important` },
        '& .MuiInputLabel-root': {
          [theme.breakpoints.down('md')]: { fontSize: '14px !important' },
        },
      }}
      data-testid={name}
    />
  );
};
